const statusColors = ["#f59e0b", "#2563eb", "#16a34a", "#dc2626", "#7c3aed", "#0891b2"];

const formatAmount = (amount) =>
  `${new Intl.NumberFormat("ru-RU").format(Math.round(amount))} ₸`;

const sumAmounts = (payments) =>
  payments.reduce((total, payment) => total + Number(payment.amount || 0), 0);

const groupTopValues = (payments, getValue) => {
  const grouped = payments.reduce((items, payment) => {
    const value = getValue(payment) || "Не указано";

    if (!items[value]) {
      items[value] = { label: value, count: 0, amount: 0 };
    }

    items[value].count += 1;
    items[value].amount += Number(payment.amount || 0);

    return items;
  }, {});

  return Object.values(grouped)
    .sort((left, right) => right.amount - left.amount)
    .slice(0, 5);
};

export default function ReportsPage({ payments, templateColumns, paymentActions, actionColumns }) {
  const actionMeta = actionColumns.flatMap((column) =>
    column.options.map((option) => ({
      key: `${column.id}:${option.value}`,
      columnId: column.id,
      value: option.value,
      label: `${column.label}: ${option.label}`
    }))
  );
  const statusMeta = (actionMeta.length ? actionMeta : paymentActions).map((action, index) => ({
    ...action,
    key: action.key || action.status,
    color: statusColors[index % statusColors.length]
  }));
  const totalAmount = sumAmounts(payments);
  const paidAction = statusMeta.find((action) =>
    action.label.toLowerCase().includes("опла")
  ) || statusMeta[2];
  const pendingAction = statusMeta[0];
  const filterByAction = (payment, action) =>
    action.columnId
      ? payment.extra_fields?.[action.columnId] === action.value
      : payment.status === action.status;
  const paidPayments = paidAction
    ? payments.filter((payment) => filterByAction(payment, paidAction))
    : [];
  const paidAmount = sumAmounts(paidPayments);
  const pendingAmount = sumAmounts(
    pendingAction
      ? payments.filter((payment) => filterByAction(payment, pendingAction))
      : []
  );
  const averageAmount = payments.length ? totalAmount / payments.length : 0;
  const paidPercent = totalAmount ? Math.round((paidAmount / totalAmount) * 100) : 0;
  const statusData = statusMeta.map((status) => {
    const statusPayments = payments.filter((payment) => filterByAction(payment, status));

    return {
      ...status,
      count: statusPayments.length,
      amount: sumAmounts(statusPayments),
      percent: payments.length
        ? Math.round((statusPayments.length / payments.length) * 100)
        : 0
    };
  });

  const dateColumn = templateColumns.find((column) => column.format === "date");
  const datedPayments = payments.filter((payment) =>
    dateColumn && payment.extra_fields?.[dateColumn.id]
  );
  const dynamics = Object.values(
    datedPayments.reduce((days, payment) => {
      const date = payment.extra_fields[dateColumn.id];

      if (!days[date]) {
        days[date] = { date, amount: 0, count: 0 };
      }

      days[date].amount += Number(payment.amount || 0);
      days[date].count += 1;

      return days;
    }, {})
  ).sort((left, right) => left.date.localeCompare(right.date)).slice(-10);
  const maxDynamicAmount = Math.max(...dynamics.map((day) => day.amount), 1);
  const cities = groupTopValues(payments, (payment) => payment.branch);
  const contractors = groupTopValues(payments, (payment) => payment.contractor);
  const maxCityAmount = Math.max(...cities.map((city) => city.amount), 1);
  const maxContractorAmount = Math.max(
    ...contractors.map((contractor) => contractor.amount),
    1
  );

  return (
    <div className="reports-page">
      <div className="reports-head">
        <div>
          <h1>Отчеты</h1>
          <p>Аналитика по платежам и действиям</p>
        </div>

        <div className="report-period">Текущие данные</div>
      </div>

      <div className="report-kpis">
        <div className="report-kpi">
          <span>Общая сумма</span>
          <strong>{formatAmount(totalAmount)}</strong>
          <small>{payments.length} платежей</small>
        </div>

        <div className="report-kpi paid">
          <span>{paidAction?.label || "Оплачено"}</span>
          <strong>{formatAmount(paidAmount)}</strong>
          <small>{paidPercent}% от общей суммы</small>
        </div>

        <div className="report-kpi pending">
          <span>{pendingAction?.label || "Первое действие"}</span>
          <strong>{formatAmount(pendingAmount)}</strong>
          <small>По первому действию</small>
        </div>

        <div className="report-kpi">
          <span>Средний платеж</span>
          <strong>{formatAmount(averageAmount)}</strong>
          <small>Среднее значение</small>
        </div>
      </div>

      <div className="report-grid">
        <section className="report-section report-statuses">
          <h2>Распределение по действиям</h2>

          {statusData.map((status) => (
            <div className="status-report-row" key={status.key}>
              <div className="report-row-label">
                <span>{status.label}</span>
                <strong>{status.count}</strong>
              </div>
              <div className="report-track">
                <span
                  style={{
                    width: `${status.percent}%`,
                    backgroundColor: status.color
                  }}
                />
              </div>
              <small>{formatAmount(status.amount)}</small>
            </div>
          ))}
        </section>

        <section className="report-section report-dynamics">
          <h2>Динамика платежей</h2>

          {dynamics.length > 0 ? (
            <div className="dynamic-chart">
              {dynamics.map((day) => (
                <div className="dynamic-column" key={day.date}>
                  <span>{formatAmount(day.amount)}</span>
                  <div
                    className="dynamic-bar"
                    style={{ height: `${(day.amount / maxDynamicAmount) * 150}px` }}
                  />
                  <strong>{day.date.slice(5)}</strong>
                </div>
              ))}
            </div>
          ) : (
            <p className="report-empty">Для динамики нужны платежи с датой.</p>
          )}
        </section>

        <section className="report-section">
          <h2>Города по объему платежей</h2>

          {cities.map((city) => (
            <div className="leader-row" key={city.label}>
              <div>
                <strong>{city.label}</strong>
                <small>{city.count} платежей</small>
              </div>
              <div className="leader-amount">
                <span>{formatAmount(city.amount)}</span>
                <i style={{ width: `${(city.amount / maxCityAmount) * 100}%` }} />
              </div>
            </div>
          ))}
        </section>

        <section className="report-section">
          <h2>Крупные контрагенты</h2>

          {contractors.map((contractor) => (
            <div className="leader-row" key={contractor.label}>
              <div>
                <strong>{contractor.label}</strong>
                <small>{contractor.count} платежей</small>
              </div>
              <div className="leader-amount">
                <span>{formatAmount(contractor.amount)}</span>
                <i
                  style={{
                    width: `${(contractor.amount / maxContractorAmount) * 100}%`
                  }}
                />
              </div>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
